import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, Card, CardContent, Grid } from '@mui/material'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { gsap } from 'gsap'

import { chartConfig } from '../../../consts'
import styles from './EnhancedMetricsCharts.module.scss'
import MetricsLineChart from './MetricsLineChart'

const GRADIENT_COLORS = chartConfig.gradientColors

const handleTimeTooltipFormatter = (payload) => {
  return payload.map((entry) => {
    if (entry.name === 'Call Minutes') {
      return { ...entry, value: Math.round(entry.value) }
    }
    return entry
  })
}

const EnhancedMetricsCharts = ({
  lineChartData,
  metricsLoading,
  chartLoading
}) => {
  const chartsRef = useRef(null)
  const [hasAnimated, setHasAnimated] = useState(false)

  const callsData = lineChartData?.callsPerDay || []
  const handleTimeData = lineChartData?.handleTimePerDay || []

  const hasAnyData = callsData.length > 0 || handleTimeData.length > 0

  /* eslint-disable react-hooks/set-state-in-effect -- animation state management with GSAP */
  useEffect(() => {
    setHasAnimated(false)
  }, [metricsLoading])

  useEffect(() => {
    if (!metricsLoading && chartsRef.current && hasAnyData && gsap && !hasAnimated) {
      try {
        if (typeof gsap.timeline === 'function') {
          const tl = gsap.timeline()

          if (typeof tl.fromTo === 'function') {
            tl.fromTo(
              chartsRef.current.querySelectorAll('.chart-card'),
              { y: 24, opacity: 0 },
              {
                y: 0,
                opacity: 1,
                duration: 0.6,
                stagger: 0.15,
                ease: 'power2.out'
              }
            )
          }

          setHasAnimated(true)
        }
      } catch (error) {
      }
    }
  }, [metricsLoading, hasAnyData, hasAnimated])
  /* eslint-enable react-hooks/set-state-in-effect */

  if (metricsLoading) {
    return (
      <div className={styles.root}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={`${styles.chartCard} ${styles.callsChart}`}>
              <Box className={styles.chartHeader}>
                <TrendingUpIcon />
                <Typography variant='h6' component='h2' className={styles.chartTitle}>
                  Call Volume Trends
                </Typography>
              </Box>
              <CardContent>
                <Skeleton
                  height={350}
                  className={styles.skeletonChart}
                  aria-busy='true'
                  aria-label='Loading chart data'
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card className={`${styles.chartCard} ${styles.handleTimeChart}`}>
              <CardContent>
                <Skeleton
                  height={380}
                  className={styles.skeletonChart}
                  aria-busy='true'
                  aria-label='Loading chart data'
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    )
  }

  return (
    <div className={`${styles.root} ${styles.mainChartsContainer}`} ref={chartsRef}>
      <div className={styles.chartsContainer}>
        {/* Calls Per Day Chart */}
        <MetricsLineChart
          title='Daily Interaction Count (Inbound & Outbound)'
          chartData={callsData}
          dataKey='calls'
          dataName='Daily Calls'
          gradientId='colorCalls'
          gradientColors={GRADIENT_COLORS.calls}
          loading={chartLoading}
          classes={styles}
          paperClassName={styles.callsPerDayPaper}
        />

        {/* Handle Time per Day Chart */}
        <MetricsLineChart
          title='Daily Interaction Minutes (Inbound & Outbound)'
          chartData={handleTimeData}
          dataKey='minutes'
          dataName='Call Minutes'
          gradientId='colorMinutes'
          gradientColors={GRADIENT_COLORS.minutes}
          loading={chartLoading}
          classes={styles}
          paperClassName={styles.handleTimePaper}
          formatTooltip={handleTimeTooltipFormatter}
          yAxisFormatter={(value) => Math.round(value)}
        />
      </div>
    </div>
  )
}

export default EnhancedMetricsCharts
